import { ThemeEvents } from '@theme/events';

class CartShippingProgress extends HTMLElement {
  #observer;
  #isWriting = false;
  #abortController;

  connectedCallback() {
    this.threshold = Number(this.dataset.threshold) || 0;
    this.refresh = this.refresh.bind(this);
    this.#showLoadingState();
    this.#watchForExternalRewrites();
    this.refresh();
    document.addEventListener(ThemeEvents.cartUpdate, this.refresh);
    document.addEventListener(ThemeEvents.discountUpdate, this.refresh);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.refresh);
    document.removeEventListener(ThemeEvents.discountUpdate, this.refresh);
    this.#observer?.disconnect();
    this.#abortController?.abort();
  }

  #showLoadingState() {
    const message = this.querySelector('[data-message]');
    if (message && !message.textContent.trim()) {
      message.textContent = this.dataset.fallbackMessage || '';
    }
  }

  #watchForExternalRewrites() {
    this.#observer = new MutationObserver(() => {
      if (this.#isWriting) return;
      this.refresh();
    });
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
    });
  }

  async refresh() {
    // Cancel any in-flight request so a slow, stale fetch can never
    // overwrite what a newer one just wrote.
    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        signal: abortController.signal,
      });
      const cart = await response.json();

      if (abortController.signal.aborted) return;

      const message = this.querySelector('[data-message]');
      const fill = this.querySelector('[data-fill]');
      const track = this.querySelector('[role="progressbar"]');
      if (!message || !fill || !track) return;

      const total = Number(cart.total_price) || 0;
      const threshold = this.threshold > 0 ? this.threshold : 9900;
      const remaining = Math.max(threshold - total, 0);
      const unlocked = remaining === 0;

      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: cart.currency || this.dataset.currency || 'GBP',
      });

      const template = unlocked ? this.dataset.successMessage : this.dataset.progressMessage;

      this.#isWriting = true;
      message.innerHTML = template.replace(
        '__AMOUNT__',
        `<strong>${formatter.format(remaining / 100)}</strong>`
      );
      fill.style.width = Math.min((total / threshold) * 100, 100) + '%';
      fill.dataset.unlocked = unlocked;
      track.setAttribute('aria-valuenow', String(Math.min(total, threshold)));
      requestAnimationFrame(() => { this.#isWriting = false; });
    } catch (error) {
      if (error.name === 'AbortError') return; // superseded by a newer refresh, ignore
      const message = this.querySelector('[data-message]');
      if (message) message.textContent = this.dataset.fallbackMessage || '';
    }
  }
}

if (!customElements.get('cart-shipping-progress')) {
  customElements.define('cart-shipping-progress', CartShippingProgress);
}